/**
 * Decision + DelegateAction — the two cross-cutting commitment affordances
 * (Workflow Maps §4, CP-7/CP-8; Design System §2.8). Built once, reused on
 * EVERY object page and in the Inbox/Agents queue, because the two-party state
 * machine (someone promises → a *different* someone confirms) recurs on every
 * kind (Object Model §5).
 *
 *   • Decision — the `review → fulfilled` (or bounce) moment: an accept/reject
 *     button pair with inline evidence. Its read-only mode IS the `GateStatus`
 *     (OQ-19): show the resolved verdict + the two-party gate (owner → verifier)
 *     instead of buttons, so the same component renders both "decide" and
 *     "already decided".
 *   • DelegateAction — "hand this to an agent" (CP-8): the single affordance
 *     that turns any commitment into agent work.
 *
 * Pure presentation: the parent owns the mutation (and any reject-reason prompt
 * or agent picker). These never fetch.
 */

import * as React from "react";
import { Button } from "../button";
import { IntentBadge, StatusBadge } from "./status-badge";
import { Bot } from "../icons";
import { cn } from "../utils";

export interface DecisionProps {
  /** Headline question, e.g. "Verify this outcome?" */
  prompt?: React.ReactNode;
  /** Inline evidence (diff summary, links, the asserted outcome). */
  evidence?: React.ReactNode;
  acceptLabel?: string;
  rejectLabel?: string;
  editLabel?: string;
  onAccept?: () => void;
  onReject?: () => void;
  /**
   * Optional third verb (verification-loop P2-U1): "accept with a correction" —
   * the worker-verdict surfaces' `edit` path. Rendered as a tertiary (ghost)
   * button only when a handler is supplied; the parent owns the correction
   * prompt and the mutation, same as accept/reject.
   */
  onEdit?: () => void;
  busy?: boolean;
  disabled?: boolean;
  /**
   * Read-only `GateStatus` mode: render the resolved verdict + the two-party
   * gate instead of buttons. Used wherever a decision has already been made.
   */
  readOnly?: boolean;
  verdict?: "pending" | "accepted" | "rejected" | "edited";
  /**
   * Verdict HISTORY (VERIFICATION-SURFACE-PRD R1, #45973).
   *
   * ⚠️ `verdict` above is a SCALAR — the TERMINAL state. The vault holds a
   * SEQUENCE (`dod_judgment_receipts`), and 57 tasks carry both a reject and a
   * later approve. Rendering only the scalar headlines a rejected-and-reworked
   * task as a clean single-pass "accepted", which is exactly how a rubber stamp
   * looks. #45872 fixed the same defect one level down by adding a verdicts
   * TABLE; this renders the fact at the altitude a human actually reads.
   *
   * Optional and absent-by-default: every existing consumer keeps today's
   * single-verdict gate row unchanged.
   */
  rework?: {
    /** How many `reject` verdicts precede the terminal one. 0 → renders nothing. */
    rejectCount: number;
    /** The most recent reject's note, shown clamped to two lines. */
    lastRejectNote?: string | null;
    /** Who rejected — the verifier id, not the shipper. */
    lastRejectVerifier?: string | null;
  } | null;
  /** The accountable owner / shipper (left of the gate arrow). */
  ownerLabel?: string | null;
  /** The verifier — must differ from the owner (the ship/verify gate). */
  verifierLabel?: string | null;
  className?: string;
}

/** owner → verifier, the two-party gate line shared by both modes. */
function GateParties({
  ownerLabel,
  verifierLabel,
}: {
  ownerLabel?: string | null;
  verifierLabel?: string | null;
}) {
  if (!ownerLabel && !verifierLabel) return null;
  return (
    <div className="flex items-center gap-1.5 type-subtext text-muted-foreground">
      <span className="truncate">{ownerLabel ?? "—"}</span>
      <span aria-hidden>→</span>
      <span className="truncate">{verifierLabel ?? "awaiting verifier"}</span>
    </div>
  );
}

/**
 * The rework strip — "Accepted after 1 reject" + the reject that caused it.
 * Rendered ABOVE the evidence and the gate row, because the fact that work was
 * bounced is the load-bearing one: it is what distinguishes a verification
 * system from a rubber stamp, and it must not sit below a bundle hash.
 */
function ReworkSummary({
  rework,
  verdict,
}: {
  rework: NonNullable<DecisionProps["rework"]>;
  verdict: DecisionProps["verdict"];
}) {
  const n = rework.rejectCount;
  const plural = n === 1 ? "reject" : "rejects";
  const headline =
    verdict === "accepted"
      ? `Accepted after ${n} ${plural}`
      : verdict === "rejected"
        ? `Rejected — ${n} ${plural} on record`
        : `${n} ${plural} on record`;
  const note = rework.lastRejectNote?.trim();
  return (
    <div className="flex flex-col gap-1 rounded-md border border-destructive/40 bg-destructive/5 p-2">
      <IntentBadge intent={verdict === "rejected" ? "danger" : "warning"} withDot>
        {headline}
      </IntentBadge>
      {note ? (
        <p className="line-clamp-2 type-subtext text-muted-foreground" title={note}>
          <span className="font-medium">
            {rework.lastRejectVerifier ?? "verifier"}:
          </span>{" "}
          {note}
        </p>
      ) : null}
    </div>
  );
}

export function Decision({
  prompt,
  evidence,
  acceptLabel = "Approve",
  rejectLabel = "Reject",
  editLabel = "Edit…",
  onAccept,
  onReject,
  onEdit,
  busy = false,
  disabled = false,
  readOnly = false,
  verdict = "pending",
  rework,
  ownerLabel,
  verifierLabel,
  className,
}: DecisionProps) {
  if (readOnly) {
    // A resolved decision keeps its evidence slot (P2-U3): the artifact link
    // belongs on the verdict, not only on the pending question. No evidence and
    // no rework → the original single gate row, unchanged for existing consumers.
    const hasRework = !!rework && rework.rejectCount > 0;
    const gateRow = (
      <div
        className={cn(
          "flex items-center justify-between gap-3",
          evidence || hasRework ? undefined : className,
        )}
      >
        <GateParties ownerLabel={ownerLabel} verifierLabel={verifierLabel} />
        {/*
         * #46109: when rework is on the record the ReworkSummary IS the
         * headline ("Accepted after N reject(s)"). A StatusBadge of the
         * terminal scalar would re-headline the same card as a bare
         * "Accepted" — the defect R1 exists to kill. Zero-reject / pending
         * decisions keep the chip.
         */}
        {hasRework ? null : <StatusBadge domain="verdict" status={verdict} withDot />}
      </div>
    );
    if (!evidence && !hasRework) return gateRow;
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {hasRework ? (
          <ReworkSummary rework={rework} verdict={verdict} />
        ) : null}
        {evidence ? (
          <div className="rounded-md border border-border bg-muted/40 p-2 type-subtext text-muted-foreground">
            {evidence}
          </div>
        ) : null}
        {gateRow}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {prompt ? <div className="type-body">{prompt}</div> : null}
      {/* A verifier deciding on already-bounced work must see it was bounced. */}
      {rework && rework.rejectCount > 0 ? (
        <ReworkSummary rework={rework} verdict={verdict} />
      ) : null}
      {evidence ? (
        <div className="rounded-md border border-border bg-muted/40 p-2 type-subtext text-muted-foreground">
          {evidence}
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-3">
        <GateParties ownerLabel={ownerLabel} verifierLabel={verifierLabel} />
        <div className="flex items-center gap-2">
          {onEdit ? (
            <Button
              variant="ghost"
              onClick={onEdit}
              disabled={disabled || busy}
            >
              {editLabel}
            </Button>
          ) : null}
          <Button
            variant="reject"
            onClick={onReject}
            disabled={disabled || busy}
          >
            {rejectLabel}
          </Button>
          <Button
            variant="accept"
            onClick={onAccept}
            disabled={disabled || busy}
          >
            {acceptLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export interface DelegateActionProps {
  /** Hand this commitment to an agent. The parent shows the picker / delegates. */
  onDelegate?: () => void;
  label?: string;
  disabled?: boolean;
  /** Repeated/inline contexts use `ghost`; a standalone affordance uses `outline`. */
  variant?: "outline" | "ghost";
  className?: string;
}

export function DelegateAction({
  onDelegate,
  label = "Delegate to agent",
  disabled = false,
  variant = "outline",
  className,
}: DelegateActionProps) {
  return (
    <Button
      variant={variant}
      onClick={onDelegate}
      disabled={disabled}
      className={className}
    >
      <Bot className="size-4" />
      {label}
    </Button>
  );
}
